/**
 * Shared helpers for building exported artefact filenames (Word / PPT / PDF / Excel).
 *
 * Naming convention:
 * - Single artefact export: `ask_asper_<artefact_title>_<date>`
 * - Multi artefact export:  `ask_asper_<thread_name>_<date>`
 *
 * The `<date>` segment is a sortable stamp that includes a minified time component,
 * e.g. `20260624_0631` (24 Jun 2026, 06:31).
 */

const FILENAME_PREFIX = 'ask_asper';

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/** Sanitize a name segment for safe use in a filename (alphanumerics + single underscores). */
function sanitizeNameSegment(name: string): string {
    return name
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Date + minified time stamp for export filenames, e.g. `20260624_0631`.
 * Format: `YYYYMMDD_HHmm` (local time).
 */
export function formatExportDateStamp(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = pad2(date.getMonth() + 1);
    const day = pad2(date.getDate());
    const hours = pad2(date.getHours());
    const minutes = pad2(date.getMinutes());
    return `${year}${month}${day}_${hours}${minutes}`;
}

/**
 * Build an export filename basename (without extension) of the form
 * `ask_asper_<name>_<date>`, where `<name>` is sanitized and `<date>` is a
 * sortable stamp including a minified time, e.g. `ask_asper_revenue_summary_20260624_0631`.
 */
export function buildAskAsperExportBasename(name: string, date: Date = new Date()): string {
    const safeName = sanitizeNameSegment(name) || 'export';
    return `${FILENAME_PREFIX}_${safeName}_${formatExportDateStamp(date)}`;
}
